import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { CustomLegend } from './CustomLegend';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-duotone-svg-icons';
import {
  dimGrey,
  lightBlack,
  lightGrey,
  lightPurple,
} from '../../../mainTheme';

// One slice of the pie: `key` indexes the `data` prop, `name` is the legend
// label, ordered lowest → highest sentiment like the survey scales.
export interface SurveyPieCategory {
  name: string;
  key: string;
  color: string;
}

interface SurveyPieCardProps {
  title: string;
  categories: SurveyPieCategory[];
  data: Record<string, number>;
  isSelected?: boolean;
  onClick?: () => void;
  testId?: string;
}

const NO_DATA = {
  color: lightGrey,
  value: 100,
};

// Legends with more than four rows only fit the 152px card without a gap.
const LEGEND_ROW_LIMIT = 4;

const OnlyPercentageTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const percentage = payload[0].payload.percentage ?? 0;
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          padding: '4px 12px',
          border: 'none',
        }}
      >
        <span style={{ color: lightBlack }}>{percentage}%</span>
      </div>
    );
  }
  return null;
};

// Shared shell for the survey pie cards on the Overview page (Patient survey,
// Wellness). Both cards must look identical, so the layout lives here once.
export const SurveyPieCard: React.FC<SurveyPieCardProps> = ({
  title,
  categories,
  data,
  isSelected,
  onClick,
  testId,
}) => {
  const total = categories.reduce(
    (sum, category) => sum + (data[category.key] ?? 0),
    0,
  );
  const isNoData = total === 0;

  const chartData = categories.map((category) => {
    const value = data[category.key] ?? 0;
    return {
      name: category.name,
      value: isNoData ? 0 : value,
      percentage: isNoData ? 0 : Math.round((value / total) * 100),
      color: category.color,
    };
  });

  if (isNoData && chartData.length) {
    chartData[0].value = NO_DATA.value; // to show the no data color in pie chart
  }

  return (
    <Paper
      elevation={isSelected ? 8 : 1}
      data-testid={testId}
      sx={{
        px: '24px',
        py: '21px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        transform: isSelected ? 'scale(1.02)' : 'none',
        '&:hover': {
          elevation: 3,
        },
        borderRadius: '8px',
        border: isSelected
          ? `1px solid ${lightPurple}`
          : '1px solid var(--Outline-O5---Separator, #D4D4D9)',
        boxShadow: isSelected
          ? '-4px 8px 24px 0px rgba(0, 0, 0, 0.25)'
          : 'none',
        height: '152px',
        flex: '1 0 0',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
        }}
      >
        {/* Left section: title+icon, then legend */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
          justifyContent="space-between"
          alignItems="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                color: isSelected ? lightBlack : dimGrey,
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '0.2px',
                mr: 1,
              }}
            >
              {title}
            </Typography>
            <FontAwesomeIcon
              icon={faBadgeCheck}
              color={isSelected ? lightPurple : dimGrey}
            />
          </Box>

          <CustomLegend
            gap={categories.length > LEGEND_ROW_LIMIT ? 0 : 0.5}
            payload={chartData.map((entry) => ({
              color: entry.color,
              name: entry.name,
              value: isNoData ? 0 : entry.value,
            }))}
          />
        </Box>
        {/* Right section: pie chart only */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PieChart width={112} height={112}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={isNoData ? 0 : 55}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={isNoData ? NO_DATA.color : entry.color}
                  strokeWidth={isNoData ? 0 : 1.5}
                />
              ))}
            </Pie>
            {isNoData && (
              <>
                <circle cx="56" cy="56" r="55" fill={NO_DATA.color} />
                <text
                  x="50%"
                  y="45%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#FFF"
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: '20px',
                  }}
                >
                  No
                </text>
                <text
                  x="50%"
                  y="60%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#FFF"
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.2px',
                    lineHeight: '20px',
                  }}
                >
                  responses
                </text>
              </>
            )}
            {!isNoData && <Tooltip content={<OnlyPercentageTooltip />} />}
          </PieChart>
        </Box>
      </Box>
    </Paper>
  );
};
